import { useState, useEffect, useCallback, useRef } from 'react'
import { Plus, Pencil, Trash2, Copy, Layers, Paperclip, X, Upload, Info } from 'lucide-react'
import Modal, { FormField, FormInput, FormTextarea, FormSelect, FormActions, Button } from '../shared/Modal'
import Tooltip from '../shared/Tooltip'
import { getAPIBaseUrl } from '../../environment'
import { getAuthHeaders } from '../../services/nodeExecutors/authHelper.js'
import './TargetProfilesModal.css'

const API_BASE_URL = getAPIBaseUrl()

const LANGUAGE_OPTIONS = [
  { value: '', label: 'Select language…' },
  { value: 'Java', label: 'Java' },
  { value: 'C#', label: 'C#' },
  { value: 'C++', label: 'C++' },
  { value: 'Python', label: 'Python' },
  { value: 'Go', label: 'Go' },
  { value: 'TypeScript', label: 'TypeScript' },
  { value: 'Kotlin', label: 'Kotlin' },
]

const BUILD_TOOL_OPTIONS = [
  { value: '', label: 'None / not specified' },
  { value: 'Maven', label: 'Maven' },
  { value: 'Gradle', label: 'Gradle' },
  { value: 'MSBuild', label: 'MSBuild' },
  { value: 'CMake', label: 'CMake' },
  { value: 'npm', label: 'npm' },
  { value: 'Cargo', label: 'Cargo' },
]

const PACKAGING_OPTIONS = [
  { value: '', label: 'None / not specified' },
  { value: 'Fat JAR', label: 'Fat JAR' },
  { value: 'WAR', label: 'WAR' },
  { value: 'Docker Image', label: 'Docker Image' },
  { value: 'NuGet Package', label: 'NuGet Package' },
  { value: 'Executable', label: 'Executable' },
]

const VALIDATION_OPTIONS = [
  { value: '', label: 'None / not specified' },
  { value: 'Strict', label: 'Strict' },
  { value: 'Permissive', label: 'Permissive' },
  { value: 'Compile Only', label: 'Compile Only' },
]

const EMPTY_FORM = {
  name: '',
  description: '',
  target_language: '',
  language_version: '',
  build_tool: '',
  build_tool_version: '',
  framework: '',
  framework_version: '',
  packaging_style: '',
  validation_mode: '',
  is_default: false,
  available_in_workflow: true,
  available_in_workspace: true,
}

function profileSummaryLine(p) {
  const parts = []
  if (p.target_language) parts.push(p.target_language + (p.language_version ? ` ${p.language_version}` : ''))
  if (p.build_tool) parts.push(p.build_tool + (p.build_tool_version ? ` ${p.build_tool_version}` : ''))
  if (p.framework) parts.push(p.framework + (p.framework_version ? ` ${p.framework_version}` : ''))
  return parts.join(' • ')
}

// ── Live summary derivation from formData ─────────────────────────────────────
function buildSummaryLine(f) {
  const parts = []
  if (f.target_language) parts.push(f.target_language + (f.language_version ? ` ${f.language_version}` : ''))
  if (f.build_tool) parts.push(f.build_tool + (f.build_tool_version ? ` ${f.build_tool_version}` : ''))
  if (f.framework) parts.push(f.framework + (f.framework_version ? ` ${f.framework_version}` : ''))
  return parts.join(' • ')
}

// ── Field help icon with tooltip ──────────────────────────────────────────────
// Renders a small ◎ info icon that shows a tooltip on hover.
// tabIndex + aria-label ensure keyboard/screen-reader accessibility.
// `title` provides a native browser tooltip as keyboard fallback.
function FieldHelp({ text, placement = 'top' }) {
  if (!text) return null
  return (
    <Tooltip simple={text} placement={placement}>
      <span
        className="tp-field-help-icon"
        tabIndex={0}
        role="img"
        aria-label={text}
        title={text}
      >
        <Info size={12} />
      </span>
    </Tooltip>
  )
}

function _fmtBytes(bytes) {
  if (!bytes || bytes === 0) return '0 B'
  const k = 1024
  const units = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${units[i]}`
}

function TargetProfilesModal({ isOpen, onClose, accountId }) {
  const [activeTab, setActiveTab] = useState('list')
  const [profiles, setProfiles] = useState([])
  const [loading, setLoading] = useState(false)
  const [editingProfile, setEditingProfile] = useState(null)
  const [formData, setFormData] = useState(EMPTY_FORM)
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [globalError, setGlobalError] = useState(null)

  // File attachments (edit tab only)
  const [profileFiles, setProfileFiles] = useState([])
  const [filesLoading, setFilesLoading] = useState(false)
  const [uploadingFile, setUploadingFile] = useState(false)
  const [fileDeleteConfirm, setFileDeleteConfirm] = useState(null)
  const fileInputRef = useRef(null)

  // Language defaults prefill
  const defaultsCacheRef = useRef({})          // { 'Java': {default_language_version: '17', ...} }
  const [defaultedFields, setDefaultedFields] = useState(new Set())  // fields auto-filled by last defaults load
  const [defaultsNotice, setDefaultsNotice] = useState(null)         // 'Defaults loaded for Java' (auto-dismisses)
  const defaultsNoticeTimer = useRef(null)

  // ── Load ─────────────────────────────────────────────────────────────────────

  const loadProfiles = useCallback(async () => {
    if (!accountId) return
    setLoading(true)
    setGlobalError(null)
    try {
      const res = await fetch(
        `${API_BASE_URL}/target-profiles?account_id=${encodeURIComponent(accountId)}`,
        { headers: getAuthHeaders({ 'Content-Type': 'application/json' }) }
      )
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      setProfiles(data)
    } catch (err) {
      setGlobalError('Failed to load Target Profiles: ' + err.message)
    } finally {
      setLoading(false)
    }
  }, [accountId])

  useEffect(() => {
    if (isOpen) {
      loadProfiles()
      setActiveTab('list')
      setEditingProfile(null)
      setFormData(EMPTY_FORM)
      setErrors({})
      setGlobalError(null)
    }
  }, [isOpen, accountId]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Helpers ───────────────────────────────────────────────────────────────────

  // When user manually edits a field, remove it from the auto-fill tracking set
  // so that a subsequent language change won't overwrite their intentional edit.
  const field = (key) => (val) => {
    setFormData(prev => ({ ...prev, [key]: val }))
    setDefaultedFields(prev => { const s = new Set(prev); s.delete(key); return s })
  }
  const check = (key) => (e) => setFormData(prev => ({ ...prev, [key]: e.target.checked }))

  function openCreate() {
    setEditingProfile(null)
    setFormData(EMPTY_FORM)
    setErrors({})
    setDefaultedFields(new Set())
    setDefaultsNotice(null)
    setActiveTab('create')
  }

  function openEdit(profile) {
    setEditingProfile(profile)
    setFormData({
      name: profile.name || '',
      description: profile.description || '',
      target_language: profile.target_language || '',
      language_version: profile.language_version || '',
      build_tool: profile.build_tool || '',
      build_tool_version: profile.build_tool_version || '',
      framework: profile.framework || '',
      framework_version: profile.framework_version || '',
      packaging_style: profile.packaging_style || '',
      validation_mode: profile.validation_mode || '',
      is_default: profile.is_default || false,
      available_in_workflow: profile.available_in_workflow !== false,
      available_in_workspace: profile.available_in_workspace !== false,
    })
    setErrors({})
    setProfileFiles([])
    setFileDeleteConfirm(null)
    loadProfileFiles(profile.profile_id)
    setActiveTab('edit')
  }

  function cancelForm() {
    setEditingProfile(null)
    setFormData(EMPTY_FORM)
    setErrors({})
    setProfileFiles([])
    setFileDeleteConfirm(null)
    setDefaultedFields(new Set())
    setDefaultsNotice(null)
    if (defaultsNoticeTimer.current) clearTimeout(defaultsNoticeTimer.current)
    setActiveTab('list')
  }

  // ── Language defaults prefill ──────────────────────────────────────────────

  async function handleLanguageChange(newLang) {
    // Update language immediately
    setFormData(prev => ({ ...prev, target_language: newLang }))
    setDefaultsNotice(null)
    if (defaultsNoticeTimer.current) clearTimeout(defaultsNoticeTimer.current)

    if (!newLang) {
      setDefaultedFields(new Set())
      return
    }

    // Fetch from DB (cached in a ref — survives re-renders, not reactive)
    let defaults = defaultsCacheRef.current[newLang]
    if (!defaults) {
      try {
        const res = await fetch(
          `${API_BASE_URL}/target-profile-defaults/${encodeURIComponent(newLang)}`,
          { headers: getAuthHeaders({ 'Content-Type': 'application/json' }) }
        )
        if (res.ok) {
          defaults = await res.json()
          defaultsCacheRef.current[newLang] = defaults
        } else if (res.status === 404) {
          return   // No defaults — silently do nothing
        }
      } catch (_e) {
        return   // Network error — defaults are optional
      }
    }

    if (!defaults) return

    // Read current form state via the closure snapshot (formData captured at call time).
    // For all DEFAULTABLE fields (not target_language itself), formData is still accurate
    // since we only changed target_language above and haven't touched the others.
    const DEFAULTABLE = [
      'language_version', 'build_tool', 'build_tool_version',
      'framework', 'framework_version', 'packaging_style', 'validation_mode',
    ]

    const updates = {}
    const newDefaulted = new Set()

    for (const f of DEFAULTABLE) {
      const dbVal = defaults[`default_${f}`] || ''
      const currentVal = formData[f]
      // Apply if: field is empty (e.g. new profile) OR it was auto-filled last time
      // (meaning the user hasn't manually changed it — safe to update to new language's value).
      if (!currentVal || defaultedFields.has(f)) {
        updates[f] = dbVal
        if (dbVal) newDefaulted.add(f)
      }
    }

    if (Object.keys(updates).length > 0) {
      setFormData(prev => ({ ...prev, ...updates }))
    }
    setDefaultedFields(newDefaulted)
    setDefaultsNotice(`Defaults loaded for ${defaults.display_name}`)
    defaultsNoticeTimer.current = setTimeout(() => setDefaultsNotice(null), 4000)
  }

  // ── Validate ──────────────────────────────────────────────────────────────────

  function validate() {
    const e = {}
    if (!formData.name.trim()) e.name = 'Profile Name is required'
    if (!formData.target_language) e.target_language = 'Target Language is required'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  // ── Save ──────────────────────────────────────────────────────────────────────

  async function handleSave() {
    if (!validate()) return
    setSaving(true)
    setGlobalError(null)
    try {
      const body = {
        account_id: accountId,
        name: formData.name.trim(),
        target_language: formData.target_language,
        description: formData.description || null,
        language_version: formData.language_version || null,
        build_tool: formData.build_tool || null,
        build_tool_version: formData.build_tool_version || null,
        framework: formData.framework || null,
        framework_version: formData.framework_version || null,
        packaging_style: formData.packaging_style || null,
        validation_mode: formData.validation_mode || null,
        is_default: formData.is_default,
        available_in_workflow: formData.available_in_workflow,
        available_in_workspace: formData.available_in_workspace,
      }

      let res
      if (editingProfile) {
        res = await fetch(`${API_BASE_URL}/target-profiles/${editingProfile.profile_id}`, {
          method: 'PATCH',
          headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify(body),
        })
      } else {
        res = await fetch(`${API_BASE_URL}/target-profiles`, {
          method: 'POST',
          headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify(body),
        })
      }

      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.detail || `HTTP ${res.status}`)
      }

      await loadProfiles()
      cancelForm()
    } catch (err) {
      setGlobalError('Failed to save: ' + err.message)
    } finally {
      setSaving(false)
    }
  }

  // ── Delete ────────────────────────────────────────────────────────────────────

  async function handleDeleteConfirmed() {
    if (!deleteConfirm) return
    setSaving(true)
    try {
      const res = await fetch(`${API_BASE_URL}/target-profiles/${deleteConfirm.profile_id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      })
      if (!res.ok && res.status !== 204) throw new Error(`HTTP ${res.status}`)
      await loadProfiles()
      setDeleteConfirm(null)
      if (activeTab === 'edit' && editingProfile?.profile_id === deleteConfirm.profile_id) {
        cancelForm()
      }
    } catch (err) {
      setGlobalError('Failed to delete: ' + err.message)
      setDeleteConfirm(null)
    } finally {
      setSaving(false)
    }
  }

  // ── Copy ──────────────────────────────────────────────────────────────────────

  async function handleCopyProfile(profile) {
    setSaving(true)
    setGlobalError(null)
    try {
      const res = await fetch(`${API_BASE_URL}/target-profiles/${profile.profile_id}/copy`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.detail || `HTTP ${res.status}`)
      }
      await loadProfiles()
    } catch (err) {
      setGlobalError('Failed to copy profile: ' + err.message)
    } finally {
      setSaving(false)
    }
  }

  // ── File attachments ──────────────────────────────────────────────────────────

  const loadProfileFiles = useCallback(async (profile_id) => {
    if (!profile_id) return
    setFilesLoading(true)
    try {
      const res = await fetch(
        `${API_BASE_URL}/target-profiles/${profile_id}/files`,
        { headers: getAuthHeaders({ 'Content-Type': 'application/json' }) }
      )
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      setProfileFiles(await res.json())
    } catch (err) {
      setGlobalError('Failed to load attachments: ' + err.message)
    } finally {
      setFilesLoading(false)
    }
  }, [])

  async function handleFileSelected(e) {
    const file = e.target.files?.[0]
    if (!file || !editingProfile) return
    // Reset input so the same file can be re-uploaded after removal
    e.target.value = ''

    setUploadingFile(true)
    setGlobalError(null)
    try {
      const formDataPayload = new FormData()
      formDataPayload.append('file', file)
      formDataPayload.append('account_id', accountId)

      // Don't include Content-Type — browser sets multipart boundary automatically
      const authHeaders = { ...getAuthHeaders() }
      delete authHeaders['Content-Type']

      const res = await fetch(
        `${API_BASE_URL}/target-profiles/${editingProfile.profile_id}/files`,
        { method: 'POST', headers: authHeaders, body: formDataPayload }
      )
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.detail || `HTTP ${res.status}`)
      }
      await loadProfileFiles(editingProfile.profile_id)
    } catch (err) {
      setGlobalError('Upload failed: ' + err.message)
    } finally {
      setUploadingFile(false)
    }
  }

  async function handleFileDeleteConfirmed() {
    if (!fileDeleteConfirm || !editingProfile) return
    setSaving(true)
    try {
      const res = await fetch(
        `${API_BASE_URL}/target-profiles/${editingProfile.profile_id}/files/${fileDeleteConfirm.file_id}`,
        { method: 'DELETE', headers: getAuthHeaders() }
      )
      if (!res.ok && res.status !== 204) throw new Error(`HTTP ${res.status}`)
      setFileDeleteConfirm(null)
      await loadProfileFiles(editingProfile.profile_id)
    } catch (err) {
      setGlobalError('Delete failed: ' + err.message)
      setFileDeleteConfirm(null)
    } finally {
      setSaving(false)
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────────

  const showEditTab = !!editingProfile
  const isFormTab = activeTab === 'create' || activeTab === 'edit'
  const summaryLine = buildSummaryLine(formData)
  const hasSummaryContent = !!(formData.name || formData.target_language)

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Target Profiles" size="xlarge">
      {/* tp-layout cancels modal-body padding and owns its own layout */}
      <div className="tp-layout">

        {/* ── Fixed top: subtitle + tabs ── */}
        <div className="tp-modal-top">
          <p className="tp-subtitle">
            Reusable target environment profiles — store your language, build tool, and framework
            settings once, then reuse across Workflow and Workspace.
          </p>
          <div className="tp-tabs">
            <button
              className={`tp-tab ${activeTab === 'list' ? 'tp-tab-active' : ''}`}
              onClick={() => setActiveTab('list')}
            >
              Profiles
              {profiles.length > 0 && (
                <span className="tp-tab-count">{profiles.length}</span>
              )}
            </button>
            <button
              className={`tp-tab ${activeTab === 'create' ? 'tp-tab-active' : ''}`}
              onClick={openCreate}
            >
              New Profile
            </button>
            {showEditTab && (
              <button
                className={`tp-tab ${activeTab === 'edit' ? 'tp-tab-active' : ''}`}
                onClick={() => setActiveTab('edit')}
              >
                Edit Profile
              </button>
            )}
          </div>
        </div>

        {/* ── Scrollable body ── */}
        <div className="tp-scroll-body">
          {globalError && (
            <div className="tp-error-banner">{globalError}</div>
          )}

          {/* ── LIST TAB ── */}
          {activeTab === 'list' && (
            <div className="tp-list-container">
              {loading && (
                <div className="tp-loading">Loading profiles…</div>
              )}

              {!loading && !accountId && (
                <div className="tp-empty-state">
                  <div className="tp-empty-icon">
                    <Layers size={36} />
                  </div>
                  <div className="tp-empty-text">
                    <strong>No account selected</strong>
                    <span>Select an account above to manage its Target Profiles.</span>
                  </div>
                </div>
              )}

              {!loading && accountId && profiles.length === 0 && (
                <div className="tp-empty-state">
                  <div className="tp-empty-icon">
                    <Layers size={36} />
                  </div>
                  <div className="tp-empty-text">
                    <strong>No Target Profiles yet</strong>
                    <span>
                      Target Profiles store your generation and packaging settings — language,
                      build tool, and framework — so you can reuse them across Workflow and Workspace.
                    </span>
                    <span className="tp-example-hint">
                      e.g.&nbsp; Java 17 &bull; Maven 3.9 &bull; Spring Boot 3.2
                    </span>
                  </div>
                  <Button variant="primary" onClick={openCreate}>
                    <Plus size={14} /> Create Target Profile
                  </Button>
                </div>
              )}

              {!loading && profiles.length > 0 && (
                <>
                  <div className="tp-list-header">
                    <span className="tp-count">
                      {profiles.length} profile{profiles.length !== 1 ? 's' : ''}
                    </span>
                    <Button variant="primary" onClick={openCreate}>
                      <Plus size={13} /> New Profile
                    </Button>
                  </div>

                  <div className="tp-cards">
                    {profiles.map(profile => (
                      <div key={profile.profile_id} className={`tp-card ${profile.is_default ? 'tp-card-default' : ''}`}>
                        <div className="tp-card-main">
                          <div className="tp-card-header">
                            <span className="tp-card-name">{profile.name}</span>
                            <div className="tp-card-badges">
                              {profile.is_default && (
                                <span className="tp-badge tp-badge-default">Default</span>
                              )}
                            </div>
                          </div>

                          {profileSummaryLine(profile) && (
                            <div className="tp-card-summary">{profileSummaryLine(profile)}</div>
                          )}

                          {profile.description && (
                            <div className="tp-card-desc">{profile.description}</div>
                          )}
                        </div>

                        <div className="tp-card-actions">
                          <button
                            className="tp-action-btn"
                            title="Edit profile"
                            onClick={() => openEdit(profile)}
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            className="tp-action-btn"
                            title="Duplicate profile"
                            onClick={() => handleCopyProfile(profile)}
                          >
                            <Copy size={14} />
                          </button>
                          <button
                            className="tp-action-btn tp-action-danger"
                            title="Delete profile"
                            onClick={() => setDeleteConfirm(profile)}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── CREATE / EDIT FORM TAB ── */}
          {isFormTab && (
            <div className="tp-form-outer">
              {/* Main form + optional live summary */}
              <div className="tp-form-layout">

                {/* ── Left: form sections ── */}
                <div className="tp-form-main">

                  {/* Section 1: Profile Info */}
                  <div className="tp-section">
                    <h3 className="tp-section-title">Profile Info</h3>

                    <FormField
                      label={<>Profile Name <FieldHelp text="A reusable label for this set of generation settings. Shown in Workflow and Workspace dropdowns when users select a profile." /></>}
                      required
                      error={errors.name}
                    >
                      <FormInput
                        value={formData.name}
                        onChange={field('name')}
                        placeholder="e.g. Java 17 Spring Boot — Production"
                      />
                    </FormField>

                    <FormField label="Description">
                      <FormTextarea
                        value={formData.description}
                        onChange={field('description')}
                        placeholder="Optional: help users understand when to choose this profile."
                        rows={2}
                      />
                    </FormField>

                    <div className="tp-checkbox-group">
                      <label className="tp-checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.is_default}
                          onChange={check('is_default')}
                        />
                        Set as default profile for this account
                        <FieldHelp text="Only one profile per account can be the default. Choosing this will unset any existing default on this account." placement="right" />
                      </label>
                    </div>

                    <div className="tp-checkbox-row">
                      <label className="tp-checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.available_in_workflow}
                          onChange={check('available_in_workflow')}
                        />
                        Available in Workflow
                        <FieldHelp text="Makes this profile selectable in Workflow node configuration panels when running transformations." placement="right" />
                      </label>
                      <label className="tp-checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.available_in_workspace}
                          onChange={check('available_in_workspace')}
                        />
                        Available in Workspace
                        <FieldHelp text="Makes this profile selectable in the Workspace transformation view." placement="right" />
                      </label>
                    </div>
                  </div>

                  {/* Section 2: Target Platform */}
                  <div className="tp-section">
                    <h3 className="tp-section-title">Target Platform</h3>

                    {defaultsNotice && (
                      <div className="tp-defaults-notice">{defaultsNotice}</div>
                    )}

                    <div className="tp-field-row">
                      <FormField
                        label={<>Target Language <FieldHelp text="The programming language the source code will be transformed into. Selecting a language loads recommended defaults for the fields below." /></>}
                        required
                        error={errors.target_language}
                      >
                        <FormSelect
                          value={formData.target_language}
                          onChange={handleLanguageChange}
                          options={LANGUAGE_OPTIONS}
                        />
                      </FormField>

                      <FormField label={<>Language Version <FieldHelp text="Specific version of the target language to generate for, e.g. 17 for Java or .NET 8 for C#." /></>}>
                        <FormInput
                          value={formData.language_version}
                          onChange={field('language_version')}
                          placeholder="e.g. 17, .NET 8, C++17"
                        />
                      </FormField>
                    </div>

                    <div className="tp-field-row">
                      <FormField label={<>Build Tool <FieldHelp text="The tool used to build the generated project, such as Maven or Gradle. Referenced in generated build files." /></>}>
                        <FormSelect
                          value={formData.build_tool}
                          onChange={field('build_tool')}
                          options={BUILD_TOOL_OPTIONS}
                        />
                      </FormField>

                      <FormField label={<>Build Tool Version <FieldHelp text="Pinned version of the build tool added to generated config files (e.g. pom.xml or build.gradle)." /></>}>
                        <FormInput
                          value={formData.build_tool_version}
                          onChange={field('build_tool_version')}
                          placeholder="e.g. 3.9"
                        />
                      </FormField>
                    </div>

                    <div className="tp-field-row">
                      <FormField label={<>Framework <FieldHelp text="Application framework applied to the generated code, such as Spring Boot or ASP.NET Core." /></>}>
                        <FormInput
                          value={formData.framework}
                          onChange={field('framework')}
                          placeholder="e.g. Spring Boot, ASP.NET"
                        />
                      </FormField>

                      <FormField label={<>Framework Version <FieldHelp text="Specific version of the framework referenced in generated dependency declarations." /></>}>
                        <FormInput
                          value={formData.framework_version}
                          onChange={field('framework_version')}
                          placeholder="e.g. 3.2"
                        />
                      </FormField>
                    </div>
                  </div>

                  {/* Section 3: Packaging & Validation */}
                  <div className="tp-section">
                    <h3 className="tp-section-title">Packaging &amp; Validation</h3>
                    <p className="tp-section-hint">
                      Controls how the generated application is assembled and checked before delivery.
                    </p>

                    <div className="tp-field-row">
                      <FormField label={<>Packaging Style <FieldHelp text="How the built application is packaged — e.g. Fat JAR bundles all dependencies into one file; WAR targets a servlet container." /></>}>
                        <FormSelect
                          value={formData.packaging_style}
                          onChange={field('packaging_style')}
                          options={PACKAGING_OPTIONS}
                        />
                      </FormField>

                      <FormField label={<>Validation Mode <FieldHelp text="How strictly generated code is checked before packaging. Strict rejects any compilation error; Permissive may continue despite warnings; Compile Only skips deeper checks." /></>}>
                        <FormSelect
                          value={formData.validation_mode}
                          onChange={field('validation_mode')}
                          options={VALIDATION_OPTIONS}
                        />
                      </FormField>
                    </div>
                  </div>

                  {/* Section 4: Attached Files — edit tab only */}
                  {activeTab === 'edit' && (
                    <div className="tp-section">
                      <h3 className="tp-section-title">
                        <Paperclip size={11} style={{ display: 'inline', marginRight: 5, verticalAlign: 'middle' }} />
                        Build &amp; Config Files
                        <span className="tp-files-count">{profileFiles.length > 0 ? ` (${profileFiles.length})` : ''}</span>
                      </h3>
                      <p className="tp-section-hint">
                        Optional: attach pom.xml, build configs, type mappings, or other reference files.
                        These are stored in your account&apos;s S3 bucket.
                      </p>

                      {filesLoading && (
                        <div className="tp-files-loading">Loading…</div>
                      )}

                      {!filesLoading && profileFiles.length > 0 && (
                        <div className="tp-files-list">
                          {profileFiles.map(f => (
                            <div key={f.file_id} className="tp-file-row">
                              <Paperclip size={13} className="tp-file-icon" />
                              <span className="tp-file-name" title={f.original_name}>{f.original_name}</span>
                              <span className="tp-file-size">{_fmtBytes(f.size_bytes)}</span>
                              <button
                                className="tp-file-delete"
                                title="Remove file"
                                onClick={() => setFileDeleteConfirm(f)}
                              >
                                <X size={13} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="tp-files-attach">
                        <input
                          ref={fileInputRef}
                          type="file"
                          style={{ display: 'none' }}
                          onChange={handleFileSelected}
                        />
                        <button
                          className="tp-attach-btn"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadingFile}
                        >
                          <Upload size={13} />
                          {uploadingFile ? 'Uploading…' : 'Attach File'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Footer actions */}
                  <FormActions>
                    {activeTab === 'edit' && (
                      <Button
                        variant="danger"
                        onClick={() => setDeleteConfirm(editingProfile)}
                        disabled={saving}
                      >
                        Delete Profile
                      </Button>
                    )}
                    <div className="tp-actions-right">
                      <Button variant="secondary" onClick={cancelForm} disabled={saving}>
                        Cancel
                      </Button>
                      <Button variant="primary" onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving…' : (activeTab === 'edit' ? 'Save Changes' : 'Create Profile')}
                      </Button>
                    </div>
                  </FormActions>
                </div>

                {/* ── Right: live summary panel ── */}
                <div className={`tp-summary-panel ${hasSummaryContent ? 'tp-summary-panel-visible' : ''}`}>
                  <div className="tp-summary-card">
                    <div className="tp-summary-header">Profile Summary</div>

                    <div className="tp-summary-name">
                      {formData.name.trim() || <span className="tp-summary-placeholder">Untitled Profile</span>}
                    </div>

                    {summaryLine && (
                      <div className="tp-summary-line-main">{summaryLine}</div>
                    )}

                    <div className="tp-summary-rows">
                      {formData.target_language && (
                        <div className="tp-summary-row">
                          <span className="tp-summary-label">Language</span>
                          <span className="tp-summary-value">
                            {formData.target_language}{formData.language_version ? ` ${formData.language_version}` : ''}
                          </span>
                        </div>
                      )}
                      {formData.build_tool && (
                        <div className="tp-summary-row">
                          <span className="tp-summary-label">Build</span>
                          <span className="tp-summary-value">
                            {formData.build_tool}{formData.build_tool_version ? ` ${formData.build_tool_version}` : ''}
                          </span>
                        </div>
                      )}
                      {formData.framework && (
                        <div className="tp-summary-row">
                          <span className="tp-summary-label">Framework</span>
                          <span className="tp-summary-value">
                            {formData.framework}{formData.framework_version ? ` ${formData.framework_version}` : ''}
                          </span>
                        </div>
                      )}
                      {formData.packaging_style && (
                        <div className="tp-summary-row">
                          <span className="tp-summary-label">Packaging</span>
                          <span className="tp-summary-value">{formData.packaging_style}</span>
                        </div>
                      )}
                      {formData.validation_mode && (
                        <div className="tp-summary-row">
                          <span className="tp-summary-label">Validation</span>
                          <span className="tp-summary-value">{formData.validation_mode}</span>
                        </div>
                      )}
                    </div>

                    {formData.is_default && (
                      <div className="tp-summary-badge-row">
                        <span className="tp-badge tp-badge-default">Default</span>
                      </div>
                    )}

                    {!hasSummaryContent && (
                      <p className="tp-summary-empty">
                        Fill in the form to see a live preview here.
                      </p>
                    )}
                  </div>
                </div>

              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── FILE DELETE CONFIRM ── */}
      {fileDeleteConfirm && (
        <div className="tp-confirm-overlay">
          <div className="tp-confirm-dialog">
            <h4>Remove Attached File?</h4>
            <p>
              <strong>{fileDeleteConfirm.original_name}</strong> will be permanently removed
              from S3. This cannot be undone.
            </p>
            <div className="tp-confirm-actions">
              <Button variant="secondary" onClick={() => setFileDeleteConfirm(null)} disabled={saving}>
                Cancel
              </Button>
              <Button variant="danger" onClick={handleFileDeleteConfirmed} disabled={saving}>
                {saving ? 'Removing…' : 'Remove'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── DELETE CONFIRM DIALOG ── */}
      {deleteConfirm && (
        <div className="tp-confirm-overlay">
          <div className="tp-confirm-dialog">
            <h4>Delete Target Profile?</h4>
            <p>
              <strong>{deleteConfirm.name}</strong> will be permanently deleted.
              This cannot be undone.
            </p>
            <div className="tp-confirm-actions">
              <Button variant="secondary" onClick={() => setDeleteConfirm(null)} disabled={saving}>
                Cancel
              </Button>
              <Button variant="danger" onClick={handleDeleteConfirmed} disabled={saving}>
                {saving ? 'Deleting…' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  )
}

export default TargetProfilesModal
